// Grouped mode: one instance's rows as a stack of collapsible sections under its one toolbar, one result
// count and one state surface. Each section is its group header, then its own table: the instance's
// colgroup, header row and body rows, so every section shares the instance's columns, widths, order,
// visibility, density, locks and control columns. A section's table reads the same TableContext with only
// the visible rows narrowed to its group, so the header checkbox, shift-click ranges and row removal work
// within the group while selection itself spans every group.
//
// The section stack sits in the instance's one region. Each section's body is a region of its own: at 768px
// and wider the proxy scrollbar translates every section's table together (useGeometry writes the scroll
// position and lock flags onto every region), and below 768px each section scrolls sideways on its own.
// While rows load, fail or match nothing, one table holding the state surface replaces the stack.

import React, { useId } from 'react'
import PropTypes from 'prop-types'

import { Button } from '@CommonComponents/buttons'
import { TableContext, useTable } from '../context'
import { ACTIONS } from '../types'
import { Caption } from '../headers/Caption'
import { HeaderRow } from '../headers/HeaderRow'
import { tableMinWidth } from '../geometry/ColGroup'
import { BodyRows } from '../rows/BodyRow'
import { StateRow, StateSurface } from '../states/StateSurface'
import { SkeletonRows } from '../states/SkeletonRows'
import { eligibleIds } from '../selection/selection'
import { useSelectionWork } from '../selection/useSelection'
import { groupSections, shownSections } from './grouping'

/** Grouped loading renders this many skeleton rows, whatever the page size. */
export const GROUPED_SKELETON_ROWS = 6

const selectionWith = (selection, ids) => ({
  scope: ids.size ? 'rows' : 'none',
  ids,
  excluded: new Set(),
})

/**
 * What a group header's consumer content receives: the group's matching rows, how many of them are
 * selected, whether bulk work is running, and select and clear for just this group's eligible rows. Both
 * are blocked while the selection is query-scoped or bulk work runs, so a failed action leaves the
 * selection it started from.
 */
const groupApi = (table, rows, pending) => {
  const { config, state, dispatch, layout } = table
  const selection = state.selection
  const ids = layout.selectable ? eligibleIds(config, rows) : []
  const current = selection && selection.ids ? selection.ids : new Set()
  const queryScoped = Boolean(selection) && selection.scope === 'all'
  const blocked = queryScoped || pending || !ids.length
  const commit = next =>
    dispatch({
      type: ACTIONS.selectionSet,
      selection: next,
      reason: 'selection',
    })
  return {
    rows,
    count: rows.length,
    selected: queryScoped
      ? ids.length
      : ids.filter(id => current.has(id)).length,
    pending,
    selectable: layout.selectable && !blocked,
    select: () => {
      if (blocked) return
      commit(selectionWith(selection, new Set([...current, ...ids])))
    },
    clear: () => {
      if (blocked) return
      const next = new Set(current)
      ids.forEach(id => next.delete(id))
      commit(selectionWith(selection, next))
    },
  }
}

const GroupEmpty = ({ text }) => (
  <StateRow className="me-table-group-empty">
    <p className="me-table-group-empty-text">{text}</p>
  </StateRow>
)

GroupEmpty.propTypes = { text: PropTypes.string }

const GroupSection = ({ group, rows }) => {
  const table = useTable()
  const { config, groups } = table
  const { ColGroup } = table.slots
  const toggleId = useId()
  const bodyId = useId()
  const { pending } = useSelectionWork(table.refs.unit)
  const open = groups.isOpen(group)
  // the section reads the instance with its visible rows narrowed to this group, and names its group so
  // the header checkbox can say which rows it selects
  const section = {
    ...table,
    group,
    derived: { ...table.derived, pageRows: rows },
  }
  const { renderHeader } = config.grouped
  const content =
    typeof renderHeader === 'function'
      ? renderHeader(group, groupApi(section, rows, pending))
      : null
  return (
    <TableContext.Provider value={section}>
      <div className="me-table-group" data-group-key={group.key}>
        <div
          className={`me-table-group-header${group.color ? ' has-color' : ''}`}
        >
          {group.color && (
            <span
              className="me-table-group-color"
              aria-hidden="true"
              style={{ '--me-table-group-color': group.color }}
            />
          )}
          <Button
            id={toggleId}
            className="me-btn me-btn-sm me-btn-nav me-btn-row-expand me-table-group-toggle"
            icon="fa-regular fa-chevron-right"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => groups.toggle(group)}
          >
            {group.title}
          </Button>
          {content !== null && content !== undefined && (
            <div className="me-table-group-content">{content}</div>
          )}
        </div>
        <div
          id={bodyId}
          className="me-table-region me-table-group-body"
          hidden={!open}
        >
          {open && (
            <table
              className="me-table"
              aria-labelledby={toggleId}
              style={{ minWidth: `${tableMinWidth(section)}px` }}
            >
              <ColGroup />
              <HeaderRow />
              <tbody>
                {rows.length ? (
                  <BodyRows />
                ) : (
                  <GroupEmpty text={group.emptyText} />
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </TableContext.Provider>
  )
}

GroupSection.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    emptyText: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
}

// loading, error, zero data and zero results: one table with one state surface in place of the stack
const StateTable = ({ kind }) => {
  const table = useTable()
  const { ColGroup } = table.slots
  return (
    <table
      className="me-table"
      style={{ minWidth: `${tableMinWidth(table)}px` }}
      aria-busy={kind === 'loading' ? 'true' : undefined}
    >
      <Caption />
      <ColGroup />
      <HeaderRow />
      <tbody>
        {kind === 'loading' ? (
          <SkeletonRows count={GROUPED_SKELETON_ROWS} />
        ) : (
          <StateSurface kind={kind} />
        )}
      </tbody>
    </table>
  )
}

StateTable.propTypes = {
  kind: PropTypes.oneOf(['loading', 'error', 'zero-data', 'zero-results'])
    .isRequired,
}

export const GroupedTable = ({ kind }) => {
  const { config, derived } = useTable()
  if (kind !== 'rows') return <StateTable kind={kind} />
  const sections = shownSections(
    groupSections(config.grouped, derived.matching),
    derived.hasQuery
  )
  return sections.map(({ group, rows }) => (
    <GroupSection key={group.key} group={group} rows={rows} />
  ))
}

GroupedTable.propTypes = {
  kind: PropTypes.oneOf([
    'rows',
    'loading',
    'error',
    'zero-data',
    'zero-results',
  ]).isRequired,
}
